import {openDialog, Parameter, Parameters} from 'platform/components';
import {HStack} from 'platform/foundation';
import {formatPhoneNumber, useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNotNilOrEmpty, isNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import {
  GetServiceOrderApiResponse,
  useGetServiceOrderCustomerContractDetailsQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, getCustomerName} from '@dms/shared';
import {SendAfterSalesEmailForm, SendAfterSalesCustomerSmsForm} from '@dms/shared-aftersales';

import {buildArray, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';

interface OrderInformationProps extends RequiredTestIdProps {
  serviceOrder: GetServiceOrderApiResponse;
  isReadOnly?: boolean;
}

export function OrderInformation(props: OrderInformationProps) {
  const {serviceCaseId} = useWorkshopUrl();
  const formatDateTime = useDateTimeFormatter();

  const customerQuery = useGetCustomerV2Query(
    {
      customerId: props.serviceOrder?.billingInformation?.customerInformation?.customerId ?? '',
    },
    {skip: isNilOrEmpty(props.serviceOrder?.billingInformation?.customerInformation?.customerId)}
  );

  const customerContractQuery = useGetServiceOrderCustomerContractDetailsQuery(
    {
      serviceCaseId: serviceCaseId || '',
      serviceOrderId: props.serviceOrder?.id ?? '',
    },
    {skip: !serviceCaseId || !props.serviceOrder?.id}
  );

  const checkoutCustomerName = getCustomerName(customerQuery.data);
  const emailExists = isNotNilOrEmpty(props.serviceOrder?.contactInformation?.email);
  const phoneNumberExists = isNotNilOrEmpty(
    props.serviceOrder?.contactInformation?.phoneNumber?.number
  );

  const openEmailForm = () => {
    openDialog(
      <SendAfterSalesEmailForm
        serviceCaseId={serviceCaseId}
        serviceOrderId={props.serviceOrder?.id ?? ''}
        defaultValues={{defaultCategory: 'customer-message'}}
      />,
      {
        id: 'email-sms-notification-dialog',
        size: 'large',
        title: i18n.t('general.actions.sendEmail'),
      }
    );
  };

  const openSmsForm = () => {
    openDialog(
      <SendAfterSalesCustomerSmsForm
        serviceCaseId={serviceCaseId}
        serviceOrderId={props.serviceOrder?.id ?? ''}
        recordId={props.serviceOrder?.id ?? ''}
        resourceId={EntityResourceIds.serviceOrder}
      />,
      {
        title: i18n.t('general.actions.sendSms'),
        size: 'large',
        scrollBehavior: 'outside',
      }
    );
  };

  const leftParameters = buildArray<Parameter>()
    .add(() => {
      const dateInfo = match(props.serviceOrder?.state)
        .with('CLOSED', () => ({
          tooltip: i18n.t('entity.order.labels.closedAt'),
          value: props.serviceOrder?.closedAt
            ? formatDateTime('dateTimeMedium', parseDate(props.serviceOrder?.closedAt))
            : EMPTY_PLACEHOLDER,
        }))
        .with('CANCELLED', () => ({
          tooltip: i18n.t('entity.order.labels.cancelledAt'),
          value: props.serviceOrder?.cancelledAt
            ? formatDateTime('dateTimeMedium', parseDate(props.serviceOrder?.cancelledAt))
            : EMPTY_PLACEHOLDER,
        }))
        .otherwise(() => ({
          tooltip: i18n.t('general.labels.completion'),
          value: props.serviceOrder?.completionAt
            ? formatDateTime('dateTimeMedium', parseDate(props.serviceOrder?.completionAt))
            : i18n.t('entity.order.labels.noCompletionDate'),
        }));
      return {
        title: dateInfo.value,
        tooltip: dateInfo.tooltip,
        icon: 'action/date_range',
        variant: 'secondary',
      };
    })
    .add({
      title: isNotNilOrEmpty(checkoutCustomerName)
        ? checkoutCustomerName
        : i18n.t('entity.order.contactInformation.noInvoiceRecipient'),
      tooltip: i18n.t('entity.invoice.labels.recipientName'),
      icon: 'action/receipt',
      variant: 'secondary',
    })
    .add({
      title: isNotNilOrEmpty(customerContractQuery.data?.name)
        ? customerContractQuery.data?.name
        : i18n.t('entity.order.contactInformation.noContract'),
      tooltip: i18n.t('entity.order.labels.appliedCustomerContract'),
      icon: 'editor/monetization_on',
      variant: 'secondary',
    })
    .when(isNotNilOrEmpty(props.serviceOrder?.claimState), {
      title: i18n.t(`entity.order.labels.decisionOnClaim.${props.serviceOrder?.claimState}`),
      tooltip: i18n.t('entity.order.labels.decisionStatus'),
      icon: 'social/notifications',
      variant: 'secondary',
    });

  const rightParameters = buildArray<Parameter>()
    .add({
      title: emailExists
        ? props.serviceOrder?.contactInformation?.email
        : i18n.t('entity.customer.labels.noEmail'),
      tooltip: props.serviceOrder?.contactInformation?.name,
      icon: 'communication/email',
      variant: 'link',
      onClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
        openEmailForm();
      },
    })
    .add({
      title: phoneNumberExists
        ? formatPhoneNumber(
            `${props.serviceOrder?.contactInformation?.phoneNumber?.prefix ?? ''}${props.serviceOrder?.contactInformation?.phoneNumber?.number ?? ''}`
          )
        : i18n.t('entity.customer.labels.noPhoneNumber'),
      tooltip: props.serviceOrder?.contactInformation?.name,
      icon: 'communication/phone',
      variant: 'link',
      onClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
        openSmsForm();
      },
    });

  return (
    <HStack align="flex-start" justify="space-between">
      <Parameters
        parameters={leftParameters}
        data-testid={suffixTestId('contactInformation-left', props)}
        size="small"
      />
      <Parameters
        parameters={rightParameters}
        data-testid={suffixTestId('contactInformation-right', props)}
        size="small"
      />
    </HStack>
  );
}
